import { useCallback, useEffect, useRef, useState } from 'react';

import {
    E_INVOICE_RUN_STATUS_PATH,
    isRunActive,
    runEnded,
    runPollInterval,
    type EInvoiceRun,
} from '../domain/eInvoice';
import useRequest from './useRequest';

/**
 * The latest e-invoice run, read again every few seconds while one goes and every quarter of a
 * minute otherwise, so a run started by anyone (another cashier's handover) shows up. `onEnded`
 * is called when a run the page was following ends.
 */
export default function useEInvoiceRunStatus(onEnded: () => void) {
    const request = useRequest();
    const [run, setRun] = useState<EInvoiceRun | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [reads, setReads] = useState(0);
    const [readRequests, setReadRequests] = useState(0);
    const previous = useRef<EInvoiceRun | null>(null);
    const ended = useRef(onEnded);
    const active = isRunActive(run);

    ended.current = onEnded;

    const reload = useCallback(() => setReadRequests(count => count + 1), []);

    useEffect(() => {
        let cancelled = false;

        request<{ data: { run: EInvoiceRun | null } }>({ path: E_INVOICE_RUN_STATUS_PATH })
            .then(response => {
                if (cancelled) {
                    return;
                }

                if (runEnded(previous.current, response.data.run)) {
                    ended.current();
                }

                previous.current = response.data.run;
                setRun(response.data.run);
                setLoaded(true);
            })
            .catch((error: unknown) => {
                console.error('Reading the e-invoice run status failed:', error);
            })
            .finally(() => {
                if (!cancelled) {
                    setReads(count => count + 1);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [request, readRequests]);

    // Re-armed after every reading, failed ones included, so polling never stops on an error.
    useEffect(() => {
        const timer = setTimeout(reload, runPollInterval(run));

        return () => clearTimeout(timer);
    }, [reads, run, reload]);

    return { run, loaded, active, reload };
}
